import {PageAccessStorageService} from '../../../../store/page-access-storage.service';
import {TAB_NAME} from '../../../../models/tab.model';
import {FeedbackService} from '../../../../services/feedback.service';
import {AbstractControl, FormBuilder, FormGroup} from '@angular/forms';
import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {of, Subject} from 'rxjs';
import {FEEDBACK_FORM_TYPE, FEEDBACK_SHORT_DESCRIPTION, FEEDBACK_LOCATION, FEEDBACK_SOURCE, FeedbackModel} from '../../../../models/feedback';
import {catchError, takeUntil} from 'rxjs/operators';
import {AcmHttpResponse} from '../../../../models/acm-http';
import {AdaptMessageService} from '@bmc-ux/adapt-angular';

@Component({
  selector: 'app-feedback',
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.sass']
})
export class FeedbackComponent implements OnInit, OnDestroy {

  @Input() public smartNumber: string;
  @Input() public location: FEEDBACK_LOCATION | TAB_NAME;
  @ViewChild('positiveTarget') positiveTarget: ElementRef;
  @ViewChild('negativeTarget') negativeTarget: ElementRef;

  public currentFeedbackFormType: FEEDBACK_FORM_TYPE;
  public FEEDBACK_FORM_TYPE: typeof FEEDBACK_FORM_TYPE = FEEDBACK_FORM_TYPE;
  public feedbackForm: FormGroup;

  public get missingInfo(): AbstractControl {
    return this.feedbackForm.get('missingInfo');
  }

  public get featureRequest(): AbstractControl {
    return this.feedbackForm.get('featureRequest');
  }

  public get difficultToUse(): AbstractControl {
     return this.feedbackForm.get('difficultToUse');
  }

  public get appNotWorkingAsExpected(): AbstractControl {
    return this.feedbackForm.get('appNotWorkingAsExpected');
  }

  public get message(): AbstractControl {
    return this.feedbackForm.get('message');
  }

  public get feedbackStype(): string {
    const collapcedStyle = 'feedback-wrapper container';

    if (this.currentFeedbackFormType === FEEDBACK_FORM_TYPE.NEGATIVE) {
      return 'negative-feedback ' + collapcedStyle;
    } else if (this.currentFeedbackFormType === FEEDBACK_FORM_TYPE.POSITIVE) {
      return 'positive-feedback ' + collapcedStyle;
    }
    return collapcedStyle;
  }

  private onComponentDestroyed$: Subject<void> = new Subject<void>();

  constructor(private formBuilder: FormBuilder,
              private feedbackService: FeedbackService,
              private messageService: AdaptMessageService,
              private pageAccessStorageService: PageAccessStorageService) {
    this.feedbackForm = this.formBuilder.group({
      missingInfo: [null],
      featureRequest: [null],
      difficultToUse: [null],
      appNotWorkingAsExpected: [null],
      message: [null]
    });
  }

  ngOnInit(): void {
     this.currentFeedbackFormType =  FEEDBACK_FORM_TYPE.COLLAPSED;
  }

  ngOnDestroy(): void {
    this.onComponentDestroyed$.next();
    this.onComponentDestroyed$.complete();
    this.onComponentDestroyed$.unsubscribe();
  }

  public submitFeedbackForm(): void {
    const feedbackToSend: FeedbackModel = {
      smartNumber: this.smartNumber,
      description: this.location,
      shortDescription: [],
      message: this.message.value,
      negative: this.currentFeedbackFormType === FEEDBACK_FORM_TYPE.NEGATIVE,
      feedbackSource: FEEDBACK_SOURCE.ADMIN,
      author: this.pageAccessStorageService.userInfo.username,
    };
    if (this.currentFeedbackFormType === FEEDBACK_FORM_TYPE.NEGATIVE) {
      this.difficultToUse.value ? feedbackToSend.shortDescription.push(FEEDBACK_SHORT_DESCRIPTION.DIFFICULT_TO_USE) : null;
      this.missingInfo.value ? feedbackToSend.shortDescription.push(FEEDBACK_SHORT_DESCRIPTION.MISSING_INFO) : null;
      this.featureRequest.value ? feedbackToSend.shortDescription.push(FEEDBACK_SHORT_DESCRIPTION.FEATURE_REQUEST) : null;
      this.appNotWorkingAsExpected.value ? feedbackToSend.shortDescription.push(FEEDBACK_SHORT_DESCRIPTION.APP_NOT_WORKING_AS_EXPECTED) : null;
    }

    this.feedbackService.sendFeedback(feedbackToSend).pipe(
      catchError(() => of(null)),
      takeUntil(this.onComponentDestroyed$)
    )
    .subscribe((createdMasterTechnology: AcmHttpResponse): void => {
      if (!createdMasterTechnology) {
        this.messageService.add({severity: 'error', summary: 'Error', detail: `Feedback is not sent.`});
        return;
      } else {
        this.messageService.add(
          {
            severity: 'success',
            summary: 'Success',
            detail: `Your feedback was successfully submitted. Thank you.`
          }
        );
        this.currentFeedbackFormType = FEEDBACK_FORM_TYPE.COLLAPSED;
        this.feedbackForm.reset();
      }
    });
  }

  public cancelFeedbackForm(): void {
    this.currentFeedbackFormType = FEEDBACK_FORM_TYPE.COLLAPSED;
    this.feedbackForm.reset();
  }

  public onFeedbackStart(type: FEEDBACK_FORM_TYPE): void {
    this.currentFeedbackFormType = type;
    setTimeout(() => {
      if (type === FEEDBACK_FORM_TYPE.NEGATIVE) {
        this.negativeTarget.nativeElement.scrollIntoView(true);
      } else {
        this.positiveTarget.nativeElement.scrollIntoView(false);
      }
    });
  }
}
